import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Code2,
  Download,
  FileSpreadsheet,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  Plus,
  Settings,
  ShieldCheck,
  Wallet,
} from 'lucide-react';
import { useSessao } from '../../auth/SessaoContext';
import { api } from '../../api/cliente';
import type { Resumo, ResumoCreditos } from '../../api/tipos';
import { Estrutura, type GrupoNavegacao } from '../../componentes/Estrutura';
import { BotaoLink } from '../../componentes/ui';
import { numero, reais } from '../../util/formato';
import css from './Layout.module.css';
import { useCapturaLiberada } from './captura/liberacao';

/** Atualiza saldo, buscas em andamento e mensagens sem recarregar a página. */
const INTERVALO_RESUMO_MS = 15_000;

/**
 * Avisa o layout de que o saldo mudou (recarga paga, lote criado), para a
 * lateral não ficar até 15 s mostrando o valor antigo.
 */
export function avisarSaldoMudou(): void {
  window.dispatchEvent(new Event('bx:saldo'));
}

export function LayoutPainel() {
  const { usuario } = useSessao();
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [creditos, setCreditos] = useState<ResumoCreditos | null>(null);
  const [suporteNaoLidas, setSuporteNaoLidas] = useState(0);

  useEffect(() => {
    let vivo = true;
    const buscar = () => {
      void Promise.all([
        api<Resumo>('/nfe/lote/resumo'),
        api<ResumoCreditos>('/creditos'),
      ])
        .then(([r, c]) => {
          if (!vivo) return;
          setResumo(r);
          setCreditos(c);
        })
        // Falha aqui é cosmética (um contador some); não vale poluir a tela.
        .catch(() => undefined);
      // À parte: resposta do suporte não deve depender de o resumo carregar.
      void api<{ total: number }>('/suporte/nao-lidas')
        .then((r) => {
          if (vivo) setSuporteNaoLidas(r.total);
        })
        .catch(() => undefined);
    };

    buscar();
    const t = setInterval(buscar, INTERVALO_RESUMO_MS);
    window.addEventListener('bx:saldo', buscar);
    return () => {
      vivo = false;
      clearInterval(t);
      window.removeEventListener('bx:saldo', buscar);
    };
  }, []);

  const capturaLiberada = useCapturaLiberada();
  const emAndamento = resumo?.jobsEmAndamento ?? 0;
  const ehAdmin = usuario?.role === 'ADMIN';

  const grupos: GrupoNavegacao[] = [
    {
      itens: [
        {
          para: '/painel',
          rotulo: 'Visão geral',
          icone: <LayoutDashboard />,
          exato: true,
        },
      ],
    },
    {
      titulo: 'Baixar XML',
      itens: [
        { para: '/painel/downloads', rotulo: 'Downloads', icone: <Download /> },
        { para: '/painel/sped', rotulo: 'SPED', icone: <FileSpreadsheet /> },
      ],
    },
    ...(capturaLiberada
      ? [
          {
            titulo: 'Captura automática',
            itens: [
              {
                para: '/painel/empresas',
                rotulo: 'Empresas',
                icone: <Building2 />,
              },
              {
                para: '/painel/notas-recebidas',
                rotulo: 'Notas recebidas',
                icone: <Inbox />,
              },
            ],
          },
        ]
      : []),
    {
      titulo: 'Conta',
      itens: [
        { para: '/painel/creditos', rotulo: 'Créditos', icone: <Wallet /> },
        {
          para: '/painel/suporte',
          rotulo: 'Suporte',
          icone: <LifeBuoy />,
          contador: suporteNaoLidas,
        },
        {
          para: '/painel/configuracoes',
          rotulo: 'Configurações',
          icone: <Settings />,
        },
        {
          para: '/desenvolvedores',
          rotulo: 'API',
          icone: <Code2 />,
        },
      ],
    },
    ...(ehAdmin
      ? [
          {
            titulo: 'Interno',
            itens: [
              {
                para: '/admin',
                rotulo: 'Administração',
                icone: <ShieldCheck />,
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <Estrutura
      inicio="/painel"
      grupos={grupos}
      chamada={
        <BotaoLink
          to="/painel/nova-busca"
          aparencia="destaque"
          larguraTotal
          icone={<Plus />}
        >
          Nova busca
        </BotaoLink>
      }
      rodapeLateral={
        <div className={css.saldo}>
          <span className={css.saldoRotulo}>Saldo disponível</span>
          <span className={css.saldoValor}>
            {creditos ? reais(creditos.saldoCentavos) : '—'}
          </span>
          {creditos && creditos.reservadoCentavos > 0 && (
            <span className={`${css.saldoDetalhe} ${css.saldoReservado}`}>
              {reais(creditos.reservadoCentavos)} reservado em buscas
            </span>
          )}
          <span className={css.saldoDetalhe}>
            {resumo ? numero(resumo.xmlsBaixados) : '—'} XMLs baixados
          </span>
          <Link to="/painel/creditos" className={css.saldoLink}>
            Adicionar crédito <ArrowRight aria-hidden="true" />
          </Link>
        </div>
      }
      topo={
        <>
          {emAndamento > 0 && (
            <Link to="/painel/downloads" className={css.emAndamento}>
              <span className={css.pulso} aria-hidden="true" />
              {emAndamento === 1
                ? '1 busca em andamento'
                : `${emAndamento} buscas em andamento`}
            </Link>
          )}
          {creditos && (
            <Link to="/painel/creditos" className={css.saldoTopo}>
              <Wallet aria-hidden="true" />
              {reais(creditos.saldoCentavos)}
            </Link>
          )}
        </>
      }
      atalhosUsuario={[
        {
          para: '/painel/configuracoes',
          rotulo: 'Configurações',
          icone: <Settings />,
        },
        { para: '/painel/creditos', rotulo: 'Créditos', icone: <Wallet /> },
        ...(ehAdmin
          ? [
              {
                para: '/admin',
                rotulo: 'Administração',
                icone: <ShieldCheck />,
              },
            ]
          : []),
      ]}
    />
  );
}
